import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';

import { post } from '../../util/request';
import * as Styled from './FooterEmail.styled';
import SocialShare from '../SocialShare/SocialShare';
import FooterEmailForm from '../FooterEmailForm/FooterEmailForm';
import FooterEmailSuccess from '../FooterEmailSuccess/FooterEmailSuccess';
import socialLinks from './__fixtures__/socialLinks.json';
import { contacts_service_url } from '../../util/config';
import { trackEvent } from '../../util/track';
import { GET_USER_LOCALE } from '../App.gql';

/**
 * Parent component that renders either an email form or a message if the form has been submitted.
 */
const FooterEmail = ({ couponCode, isAutoApplied, codeDiscountPercentage }) => {
  const { data: localeData } = useQuery(GET_USER_LOCALE);
  const [hasFetched, setHasFetched] = useState(false);

  const handleSubmit = (values, actions) => {
    post(
      contacts_service_url,
      {
        contact: { email: values.email },
        created_source: 'footer'
      },
      { headers: { 'x-bom-country': localeData.user?.locale?.country } }
    )
      .then(() => {
        trackEvent({ event: 'footerEmailSubmit' });
        setHasFetched(true);
      })
      .catch((error) => {
        if (isAutoApplied) {
          setHasFetched(true);
        } else {
          setHasFetched(false);
          actions.setSubmitting(false);
          if (error.response) {
            actions.setErrors({ email: error.response.message });
          }
        }
      });
  };

  return (
    <>
      <Styled.Container>
        <Styled.FormWrapper>
          {hasFetched ? (
            <FooterEmailSuccess
              couponCode={couponCode}
              discountPercentage={codeDiscountPercentage}
            />
          ) : (
            <FooterEmailForm
              isAutoApplied={isAutoApplied}
              handleSubmit={handleSubmit}
              discountPercentage={codeDiscountPercentage}
            />
          )}

          <Styled.Disclaimer>
            By providing my email, l am consenting to receive Bombas emails and
            Email-Based Advertising. For additional information, please see our{' '}
            <a href="/pages/privacy-policy">Privacy Policy</a>.
          </Styled.Disclaimer>

          <Styled.Socials>
            <SocialShare socials={socialLinks} />
          </Styled.Socials>
        </Styled.FormWrapper>
      </Styled.Container>
      <Styled.TearLine />
    </>
  );
};

FooterEmail.propTypes = {
  /**
   * Coupon code to display to the user upon successful submission if enabled
   */
  couponCode: PropTypes.string,
  /**
   * If `true`, coupon is being auto applied.
   */
  isAutoApplied: PropTypes.bool,
  /**
   * Discount code percentage set in Contentful, should match percentage value set for that coupon code in Shopify
   */
  codeDiscountPercentage: PropTypes.number
};

FooterEmail.defaultProps = {
  couponCode: 'COMFORT20',
  isAutoApplied: false,
  codeDiscountPercentage: 20
};

export default FooterEmail;
